import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { detectLanguage, getTranslation } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));
  const [countdown, setCountdown] = useState(5);

  const planName = searchParams.get('plan') || 'subscription';
  
  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  useEffect(() => {
    // 5초 후 대시보드로 리다이렉트
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  const getPlanDisplayName = (plan: string) => {
    const planNames: { [key: string]: { ko: string; en: string } } = {
      personal: { ko: 'Personal 플랜', en: 'Personal Plan' },
      business: { ko: 'Business 플랜', en: 'Business Plan' },
      enterprise: { ko: 'Enterprise 플랜', en: 'Enterprise Plan' },
      subscription: { ko: '구독', en: 'subscription' }
    };
    
    return planNames[plan]?.[isKorean ? 'ko' : 'en'] || plan;
  };

  return (
    <div className="font-sans antialiased bg-gray-50 min-h-screen flex flex-col">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      {/* Success Message */}
      <section className="flex-grow flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg 
                className="w-10 h-10 text-green-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
            </div>

            {/* Success Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {isKorean ? '결제가 완료되었습니다!' : 'Payment Successful!'}
            </h1>

            {/* Success Message */}
            <p className="text-gray-600 mb-2">
              {isKorean 
                ? `${getPlanDisplayName(planName)} 구독이 성공적으로 처리되었습니다.`
                : `Your ${getPlanDisplayName(planName)} subscription has been successfully processed.`}
            </p>

            <p className="text-gray-600 mb-6">
              {isKorean 
                ? '이메일로 결제 확인서가 발송되었습니다.'
                : 'A payment confirmation has been sent to your email.'}
            </p>

            {/* Redirect Info */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                {isKorean 
                  ? `${countdown}초 후 대시보드로 이동합니다...`
                  : `Redirecting to dashboard in ${countdown} seconds...`}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:shadow-xl transition-all hover:scale-105"
              >
                {isKorean ? '대시보드로 이동' : 'Go to Dashboard'}
              </button>
              
              <button
                onClick={() => navigate('/pricing')}
                className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
              >
                {isKorean ? '요금제 보기' : 'View Plans'}
              </button>
            </div>

            {/* Next Steps */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {isKorean ? '다음 단계' : 'Next Steps'}
              </h3>
              <ul className="text-left space-y-2">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-600">
                    {isKorean 
                      ? 'AivueBiz 데스크톱 앱을 다운로드하세요'
                      : 'Download the AivueBiz desktop app'}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-600">
                    {isKorean 
                      ? '프리미엄 기능을 사용해보세요'
                      : 'Explore premium features'}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-600">
                    {isKorean 
                      ? '팀원을 초대하세요'
                      : 'Invite your team members'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Support Info */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                {isKorean 
                  ? '문의사항이 있으신가요? '
                  : 'Need help? '}
                <a href="/contact" className="text-primary-600 hover:text-primary-700 font-semibold">
                  {isKorean ? '고객 지원' : 'Contact Support'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer t={t} />
    </div>
  );
};

export default PaymentSuccessPage;